import type { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { toast } from '@tempo-io/sui-sonner';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/components/common/date-picker';
import { useTableCellMutation } from '../hooks/useTableCellMutation';
import type { ItemsTableRow } from '../types';
import { EditCell } from './EditCell';

type TargetDateField = 'earliestStartDateTime' | 'deadlineDateTime';

export const TargetDateEditCell = ({
  dateField,
  id,
  row,
  field,
  colDef,
  api,
}: GridRenderEditCellParams<ItemsTableRow> & { dateField: TargetDateField }) => {
  const cellId = `${dateField}-${id}-${field}`;
  const itemId = Number(id);
  const { t } = useTranslation();

  const { commit } = useTableCellMutation();

  const currentValue = row[dateField];

  const areDatesValid = (next: string | null): boolean => {
    if (!next) return true;
    const start = dateField === 'earliestStartDateTime' ? next : row.earliestStartDateTime;
    const finish = dateField === 'deadlineDateTime' ? next : row.deadlineDateTime;
    if (!start || !finish) return true;
    return !isAfter(start, finish);
  };

  return (
    <EditCell
      width={colDef.computedWidth}
      containerProps={{ 'data-testid': cellId }}
      contentClassName="w-auto p-0"
      matchCellHeight={false}
      closeOnScrollOutOfView={{ api, id, field }}
    >
      <DatePicker
        value={currentValue}
        showClearButton
        onSelect={(next) => {
          if (!areDatesValid(next)) {
            toast.error(t(($) => $.itemsTable.earliestStartDeadlineInvalidOrder));
            api.stopCellEditMode?.({ id, field, ignoreModifications: true });
            return;
          }
          commit({
            itemId,
            api,
            row,
            data: { [dateField]: next },
          });
          api.stopCellEditMode?.({ id, field, ignoreModifications: true });
        }}
      />
    </EditCell>
  );
};
